import { explainAiMessageStepQuery } from "@domain/ai_answers"
import { AiAuditReport } from "@domain/ai_answers/audit_report"
import useTranslate from "@hooks/use_translate"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DownloadIcon from "@mui/icons-material/Download"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt"
import ReplayIcon from "@mui/icons-material/Replay"
import SearchIcon from "@mui/icons-material/Search"
import SendIcon from "@mui/icons-material/Send"
import TroubleshootIcon from "@mui/icons-material/Troubleshoot"
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess"
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Popover,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material"
import { fmtTry, fmtUsd, getTryRate } from "@utils/currency"
import {
  buildDebugBundle,
  copyDebugBundle,
  downloadDebugBundle
} from "@utils/debug_bundle"
import {
  AiAskQuestionTypeEnum,
  AiMessages,
  AiMessageStatusEnum,
  AiMessageSteps,
  AiMessageStepStatusEnum
} from "__generated__/graphql"
import dayjs from "dayjs"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import AiAnalysisDialog from "./AiAnalysisDialog"
import AiVerdictReport from "./AiVerdictReport"
import AnswerExplanationView from "./AnswerExplanationView"
import CommerceRunTimeline from "./CommerceRunTimeline"
import SkillRunTimeline from "./SkillRunTimeline"
import StepReadableView from "./StepReadableView"
import UsedSourcesView from "./UsedSourcesView"
import V2RunOverview from "./V2RunOverview"
import { ANSWER_LABEL_KEYS, getAnswers } from "./aiMessage.utils"
import {
  cachedSearchText,
  countRenderedMatches,
  DATA_KIND_LABELS,
  DATA_KINDS,
  DataKind,
  DataRegion,
  DataSearchScope,
  extractDataRegions,
  findMatchOffsets,
  getRoleResolution,
  getRunTotals,
  getTurnTimings,
  isContainerDurationStep,
  parseJsonIfPossible,
  ROLE_DECISION_LABELS,
  ROLE_LABELS,
  isInsideRegion,
  RoleResolutionSummary,
  sectionSearchText,
  STEP_SECTIONS,
  StepSection,
  stepSectionPayload
} from "./aiStepsDrawer.utils"
import { COMMERCE_STEP_NAMES } from "./commerceRunTimeline.utils"
import { SKILL_STEP_NAMES } from "./skillRunTimeline.utils"
import { readableStepText } from "./stepCodes.labels"
import { buildReadableContext, hasReadableView } from "./stepReadable.utils"
import { buildUsedSources, hasUsedSources } from "./usedSources.utils"
import { isV2Run } from "./v2RunOverview.utils"

interface Props {
  open: boolean
  onClose: () => void
  steps: AiMessageSteps[] | undefined
  isLoading: boolean
  runId?: string | null
  /**
   * The chat turn these steps belong to. The panel covers the whole conversation, so without it
   * you lose sight of the question that came in and the answer that went out.
   */
  message?: AiMessages | null
  /** Labels the close action when the panel replaces something the user came from. */
  backLabel?: string
  initialOnlyFailed?: boolean
  navigation?: RunNavigation
}

export interface RunNavigation {
  /** 1-based, among the turns of the thread loaded so far. */
  position: number
  total: number
  hasOlderUnloaded: boolean
  onPrev?: () => void
  onNext?: () => void
}

function RunNavigator({ navigation }: { navigation: RunNavigation }) {
  const { position, total, hasOlderUnloaded, onPrev, onNext } = navigation
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        height: 30
      }}
    >
      <Tooltip title="Önceki tur" arrow>
        <span>
          <IconButton
            size="small"
            aria-label="Önceki tur"
            disabled={!onPrev}
            onClick={onPrev}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={
          hasOlderUnloaded
            ? "Sohbette yüklenen turlar içindeki sıra — daha eski turlar da var"
            : "Sohbetteki tur sırası"
        }
        arrow
      >
        <Typography
          variant="caption"
          sx={{
            px: 0.5,
            minWidth: 44,
            textAlign: "center",
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: "text.secondary"
          }}
        >
          {position} / {total}
          {hasOlderUnloaded ? "+" : ""}
        </Typography>
      </Tooltip>
      <Tooltip title="Sonraki tur" arrow>
        <span>
          <IconButton
            size="small"
            aria-label="Sonraki tur"
            disabled={!onNext}
            onClick={onNext}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

/**
 * Reading a step log means scrolling deep into it, leaving to re-read the chat, and coming back.
 * Keyed by run so each message keeps its own reading position for the session.
 */
const stepsScrollMemory = new Map<string, number>()

const CONTEXT_OPEN_KEY = "ai-steps-drawer:context-open"

function readContextOpen(): boolean {
  try {
    return window.localStorage.getItem(CONTEXT_OPEN_KEY) !== "0"
  } catch {
    return true
  }
}

function writeContextOpen(next: boolean) {
  try {
    window.localStorage.setItem(CONTEXT_OPEN_KEY, next ? "1" : "0")
  } catch {
    /* private mode / storage disabled — the panel just won't remember */
  }
}

const DATA_SCOPE_KEY = "ai-steps-drawer:data-scope"

function readDataScope(): DataSearchScope {
  try {
    const stored = window.localStorage.getItem(DATA_SCOPE_KEY)
    return stored === "incoming" ? stored : "all"
  } catch {
    return "all"
  }
}

function writeDataScope(next: DataSearchScope) {
  try {
    window.localStorage.setItem(DATA_SCOPE_KEY, next)
  } catch {
    /* storage disabled — the scope resets to "all" next time */
  }
}

type AiUsage = {
  inputToken: number
  cachedInputToken: number
  outputToken: number
  amount: number
}

type JsonAccordionProps = {
  title: string
  data: unknown
  defaultExpanded?: boolean
  expanded?: boolean
  onChange?: (next: boolean) => void
  /** Data-search term to mark inside keys and values. */
  highlight?: string
  /** Only marks inside these payload paths; unset marks everywhere. */
  highlightRegions?: ReadonlyArray<string>
  hitCount?: number
  /** `stepId:section`, the handle match navigation finds this section's marks by. */
  searchKey?: string
}

type AiStepDetail = {
  id: string
  title: string
  inputToken: number
  cachedInputToken: number
  outputToken: number
  amount: number
}

interface AiUsageMetadata {
  input_tokens?: number
  output_tokens?: number
  total_tokens?: number
  input_token_details?: { cache_read?: number }
  amount?: number
}

interface AiMetadataEntry {
  usage_metadata?: AiUsageMetadata
  amount?: number
}

interface AiResponse {
  usage?: Partial<AiUsage>
  /** Pre-`usage` runs: the raw provider payload, one entry per chunked sub-call. */
  metadata?: AiMetadataEntry[] | { chunks?: AiMetadataEntry[] }
}

/** Runs predating `response.usage` kept the counts in the raw provider payload instead. */
function legacyMetadataEntries(res: AiResponse): AiMetadataEntry[] {
  const { metadata } = res
  if (Array.isArray(metadata)) return metadata
  return metadata?.chunks ?? []
}

type SkillAuditPayload = {
  skillRunId?: string
  skillName?: string
  command?: string
  instructionAnchorIds?: string[]
  route?: string
}

function getSkillAuditPayload(response: unknown): SkillAuditPayload | null {
  if (!response || typeof response !== "object") return null

  const payload = response as SkillAuditPayload
  return payload.skillRunId ? payload : null
}

function isSkillAuditStep(step: AiMessageSteps): boolean {
  return SKILL_STEP_NAMES.includes(String(step.step))
}

function getStepTitle(step: AiMessageSteps): string {
  const skill = getSkillAuditPayload(step.response)

  if (String(step.step) === "AI_ROLE_RESOLUTION") {
    const role = getRoleResolution([step])
    return role
      ? `AI rolü · ${ROLE_LABELS[role.resolvedRole] ?? role.resolvedRole}`
      : "AI rolü"
  }

  if (String(step.step) === "SKILL_ROUTING") {
    return skill?.skillName
      ? `Skill routing · ${skill.skillName}`
      : "Skill routing"
  }
  if (String(step.step) === "SKILL_AGENT") {
    return skill?.command ? `Skill agent · ${skill.command}` : "Skill agent"
  }
  return String(step.step ?? "AI Step")
}

function isLlmStep(response: unknown): boolean {
  if (!response || typeof response !== "object") return false
  const res = response as AiResponse
  if (
    res.usage &&
    (res.usage.inputToken !== undefined || res.usage.outputToken !== undefined)
  ) {
    return true
  }
  return !!legacyMetadataEntries(res)[0]?.usage_metadata
}

const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace"
const FAILED_TINT = "rgba(211, 47, 47, 0.05)"

const DATA_SEARCH_DEBOUNCE_MS = 1000
const DATA_SEARCH_CHUNK = 8
/** Longer than the accordions' 120 ms transition, so a freshly opened section has its height. */
const EXPAND_SETTLE_MS = 160

/** `regions` limits the marks to those subtrees; without it the whole section is searched. */
type SectionHit = {
  section: StepSection
  /** 0 when the needle only matched across JSON syntax the viewer doesn't mark. */
  count: number
  regions?: ReadonlyArray<string>
}

type RegionHit = Omit<DataRegion, "value"> & { count: number }

type DataSearchResult = {
  needle: string
  all: Map<string, SectionHit[]>
  incoming: Map<string, RegionHit[]>
}

type DataKindFilter = DataKind | "any"

/** `index: null` visits the section itself — it matched, but there is no mark to land on. */
type HitOccurrence = {
  stepId: string
  section: StepSection
  index: number | null
}

const SECTION_LABELS: Record<StepSection, string> = {
  req: "Request",
  res: "Response",
  src: "Source"
}

const DATA_SCOPE_OPTIONS: {
  value: DataSearchScope
  label: string
  hint: string
}[] = [
  {
    value: "all",
    label: "Tümü",
    hint: "Adımların bütün verisi — prompt'lar, query'ler ve model çıktısı dahil"
  },
  {
    value: "incoming",
    label: "Gelen veri",
    hint: "Yalnızca dışarıdan gelen içerik: guidance satırları, bilgi kaynağı (RAG) chunk'ları, mağaza/ürün ve tool dönüşleri, pazaryeri ve HTTP yanıtları, skill talimatları. Prompt'lar, query'ler, tool argümanları ve model çıktısı aranmaz."
  }
]

const HIT_BADGE_BG = "#ffe082"

type StepCategory = "mcp" | "rag" | "skill" | "action" | "core"

type StepCategoryTone = {
  label: string
  fg: string
  bg: string
  hoverBg: string
  border: string
}

const STEP_CATEGORY_TONES: Record<StepCategory, StepCategoryTone> = {
  mcp: {
    label: "MCP",
    fg: "#286477",
    bg: "rgba(40, 100, 119, 0.055)",
    hoverBg: "rgba(40, 100, 119, 0.095)",
    border: "#78a9b5"
  },
  rag: {
    label: "RAG",
    fg: "#866b2b",
    bg: "rgba(160, 126, 44, 0.06)",
    hoverBg: "rgba(160, 126, 44, 0.1)",
    border: "#c8b77e"
  },
  skill: {
    label: "Skill",
    fg: "#76528a",
    bg: "rgba(118, 82, 138, 0.055)",
    hoverBg: "rgba(118, 82, 138, 0.095)",
    border: "#b9a4c3"
  },
  action: {
    label: "Action",
    fg: "#9a5d35",
    bg: "rgba(154, 93, 53, 0.05)",
    hoverBg: "rgba(154, 93, 53, 0.09)",
    border: "#cba38a"
  },
  core: {
    label: "Pipeline",
    fg: "#5e6d79",
    bg: "rgba(94, 109, 121, 0.035)",
    hoverBg: "rgba(94, 109, 121, 0.075)",
    border: "#afbbc2"
  }
}

const STEP_CATEGORY_ORDER: StepCategory[] = [
  "mcp",
  "rag",
  "skill",
  "action",
  "core"
]

const RAG_STEP_NAMES = new Set([
  "CHECK_IMAGE_PINECONE",
  "GET_ELASTIC_BM25_DATA",
  "GET_PINECONE_DATA",
  "FUSE_RAG_RESULTS",
  "RAG_V2_FALLBACK",
  "RERANK_CHUNKS"
])

const ACTION_STEP_NAMES = new Set(["ACTION_DETECTION", "GET_ACTIONS_INFO"])

function getStepCategory(step: AiMessageSteps): StepCategory {
  const name = String(step.step)

  if (
    name.startsWith("COMMERCE_") ||
    name === "GET_QPIEN_SHOP_PRODUCT_DETAILS" ||
    name === "MARKETPLACE_REQUEST"
  ) {
    return "mcp"
  }

  if (RAG_STEP_NAMES.has(name) || name.startsWith("ANSWER_BY_RAG_")) {
    return "rag"
  }

  if (SKILL_STEP_NAMES.includes(name)) return "skill"

  if (ACTION_STEP_NAMES.has(name) || name.startsWith("ANSWER_BY_ACTION_")) {
    return "action"
  }

  return "core"
}

function StepLegend({ steps }: { steps: AiMessageSteps[] | undefined }) {
  const categories = STEP_CATEGORY_ORDER.filter((category) =>
    (steps ?? []).some((step) => getStepCategory(step) === category)
  )

  if (categories.length < 2) return null

  return (
    <Box
      role="list"
      aria-label="Adım kategorileri"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.25,
        px: 2,
        py: 0.75,
        borderBottom: 1,
        borderColor: "divider"
      }}
    >
      {categories.map((category) => {
        const tone = STEP_CATEGORY_TONES[category]
        return (
          <Box
            key={category}
            role="listitem"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "2px",
                bgcolor: tone.border
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: tone.fg, fontWeight: 600, fontSize: 10.5 }}
            >
              {tone.label}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

function StatCell({
  label,
  value,
  tooltip
}: {
  label: string
  value: string
  tooltip?: React.ReactNode
}) {
  const cell = (
    <Box sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textTransform: "uppercase", fontSize: 10 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        sx={{ fontFamily: MONO }}
      >
        {value}
      </Typography>
    </Box>
  )

  if (!tooltip) return cell

  return (
    <Tooltip
      title={tooltip}
      arrow
      slotProps={{
        popper: { sx: { "& .MuiTooltip-tooltip": { maxWidth: 600 } } }
      }}
    >
      {cell}
    </Tooltip>
  )
}

const ROLE_TONE: Record<string, { fg: string; bg: string }> = {
  ECOMMERCE: { fg: "#6a1b9a", bg: "rgba(156, 39, 176, 0.14)" },
  MARKETPLACE: { fg: "#9a3412", bg: "rgba(217, 72, 15, 0.14)" },
  SERVICE: { fg: "#0b3d70", bg: "rgba(11, 61, 112, 0.12)" }
}

function RoleChip({ role }: { role: string }) {
  const tone = ROLE_TONE[role] ?? ROLE_TONE.SERVICE
  return (
    <Chip
      size="small"
      label={ROLE_LABELS[role] ?? role}
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        color: tone.fg,
        bgcolor: tone.bg
      }}
    />
  )
}

/** Human words, not pipeline jargon — this line is the first thing a newcomer reads. */
const CAPABILITY_LABELS: [
  keyof RoleResolutionSummary["capabilities"],
  string
][] = [
  ["rag", "Bilgi kaynağı"],
  ["action", "Action"],
  ["commerce", "Mağaza"]
]

/** "a, b ve c" — the summary line is a sentence, not a chip row. */
function joinTr(items: string[]): string {
  if (items.length < 2) return items.join("")
  return `${items.slice(0, -1).join(", ")} ve ${items[items.length - 1]}`
}

function usedCapabilities(role: RoleResolutionSummary): string[] {
  return CAPABILITY_LABELS.filter(([key]) => role.capabilities[key]).map(
    ([, label]) => label
  )
}

/**
 * The whole role story in one line: who answered, and what they were allowed to use. Everything
 * else — the other eligible roles, what enrich guessed, which rule fired — lives in the
 * AI_ROLE_RESOLUTION step, which is where someone goes once this line raises a question.
 */
function RoleLine({ role }: { role: RoleResolutionSummary }) {
  const used = usedCapabilities(role)
  const tone = ROLE_TONE[role.resolvedRole] ?? ROLE_TONE.SERVICE

  return (
    <Box
      sx={{
        mt: 0.75,
        display: "flex",
        alignItems: "baseline",
        gap: 0.75,
        flexWrap: "wrap"
      }}
    >
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        <Box component="span" sx={{ color: tone.fg, fontWeight: 700 }}>
          {ROLE_LABELS[role.resolvedRole] ?? role.resolvedRole}
        </Box>{" "}
        rolü cevapladı
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {used.length > 0
          ? `· ${joinTr(used)} kullanabildi`
          : "· ek kaynak kullanmadı"}
      </Typography>
    </Box>
  )
}

/** The drill-down, shown inside the AI_ROLE_RESOLUTION step itself. */
function RoleStepDetail({ role }: { role: RoleResolutionSummary }) {
  const used = usedCapabilities(role)
  const unused = CAPABILITY_LABELS.filter(
    ([key]) => !role.capabilities[key]
  ).map(([, label]) => label)
  const others = role.eligibleRoles.filter((r) => r !== role.resolvedRole)

  const rows: [string, React.ReactNode][] = [
    ["Cevaplayan", <RoleChip key="role" role={role.resolvedRole} />],
    ["Karar", ROLE_DECISION_LABELS[role.decision] ?? role.decision],
    ["Kullanabildiği", used.length > 0 ? used.join(", ") : "—"]
  ]

  if (unused.length > 0) rows.push(["Kapalı", unused.join(", ")])
  if (others.length > 0) {
    rows.push([
      "Diğer uygun rol",
      others.map((r) => ROLE_LABELS[r] ?? r).join(", ")
    ])
  }
  if (role.classifiedRole) {
    rows.push([
      "Enrich tahmini",
      `${ROLE_LABELS[role.classifiedRole] ?? role.classifiedRole}${
        role.contextMode ? ` · ${role.contextMode}` : ""
      }`
    ])
  }
  if (role.settledOnThisTurn) {
    rows.push(["Not", "Konuşma bu turda bu role sabitlendi"])
  }

  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px" }}
    >
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", pt: 0.25 }}
          >
            {label}
          </Typography>
          {typeof value === "string" ? (
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {value}
            </Typography>
          ) : (
            <Box>{value}</Box>
          )}
        </Box>
      ))}
    </Box>
  )
}

type StepsView = "why" | "summary" | "sources" | "raw"

const FAILURE_STRIP_LIMIT = 3

/** Where the run broke, before anything else — each line opens that raw step. */
function FailureStrip({
  steps,
  onJumpToStep
}: {
  steps: AiMessageSteps[]
  onJumpToStep: (stepId: string) => void
}) {
  if (steps.length === 0) return null
  const shown = steps.slice(0, FAILURE_STRIP_LIMIT)

  return (
    <Box
      sx={{
        px: 2,
        py: 0.75,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: FAILED_TINT
      }}
    >
      {shown.map((step) => (
        <Box
          key={String(step._id)}
          role="button"
          tabIndex={0}
          onClick={() => onJumpToStep(String(step._id))}
          onKeyDown={(e) => {
            if (e.key === "Enter") onJumpToStep(String(step._id))
          }}
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            py: 0.25,
            minWidth: 0,
            cursor: "pointer",
            borderRadius: 0.5,
            "&:hover": { bgcolor: "rgba(211, 47, 47, 0.06)" }
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "error.main", fontWeight: 700, flexShrink: 0 }}
          >
            ✕ #{step.stepList}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, flexShrink: 0, fontFamily: MONO }}
          >
            {getStepTitle(step)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {readableStepText(String(step.step), step.summary ?? step.reason)}
          </Typography>
        </Box>
      ))}
      {steps.length > shown.length && (
        <Typography variant="caption" color="text.secondary">
          +{steps.length - shown.length} hata daha — Ham adımlar sekmesinde
        </Typography>
      )}
    </Box>
  )
}

export default function AiStepsDrawer({
  open,
  onClose,
  steps,
  isLoading,
  runId,
  message,
  backLabel,
  initialOnlyFailed = false,
  navigation
}: Props) {
  const { translate } = useTranslate()
  const [tryRate, setTryRate] = useState<number>(0)
  const fetchedRef = useRef(false)

  const [query, setQuery] = useState("")
  const [onlyFailed, setOnlyFailed] = useState(initialOnlyFailed)
  const [dataQuery, setDataQuery] = useState("")
  const [dataNeedle, setDataNeedle] = useState("")
  const [dataScope, setDataScope] = useState<DataSearchScope>(readDataScope)
  const [dataKind, setDataKind] = useState<DataKindFilter>("any")
  const [dataResult, setDataResult] = useState<DataSearchResult | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [activeHit, setActiveHit] = useState(0)
  const searchTextCache = useRef<Map<string, string>>(new Map())
  const isSearchingData = dataQuery.trim() !== dataNeedle || isScanning

  const hasSkillRun = useMemo(
    () => (steps ?? []).some(isSkillAuditStep),
    [steps]
  )
  const roleResolution = useMemo(() => getRoleResolution(steps), [steps])
  const readableContext = useMemo(() => buildReadableContext(steps), [steps])
  const v2Run = useMemo(() => isV2Run(steps), [steps])
  const hasCommerceRun = useMemo(
    () =>
      (steps ?? []).some((step) =>
        COMMERCE_STEP_NAMES.includes(String(step.step))
      ),
    [steps]
  )
  const hasSummary = hasSkillRun || hasCommerceRun || v2Run
  const hasSources = useMemo(
    () => hasUsedSources(buildUsedSources(steps)),
    [steps]
  )
  const [view, setView] = useState<StepsView>(initialOnlyFailed ? "raw" : "why")
  const viewAvailable =
    view === "raw" ||
    (view === "why" && !!runId) ||
    (view === "summary" && hasSummary) ||
    (view === "sources" && hasSources)
  const activeView: StepsView = viewAvailable ? view : "raw"
  const filteredSteps = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("tr")
    return (steps ?? []).filter((step) => {
      if (onlyFailed && step.status !== AiMessageStepStatusEnum.Failed) {
        return false
      }
      if (!needle) return true

      const haystack = [
        getStepTitle(step),
        step.summary,
        step.reason,
        String(step.stepList)
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("tr")
      return haystack.includes(needle)
    })
  }, [steps, query, onlyFailed])

  const dataRegions = useMemo(
    () =>
      new Map(
        (steps ?? []).map((step) => [
          String(step._id),
          extractDataRegions(step)
        ])
      ),
    [steps]
  )

  const availableKinds = useMemo(() => {
    const present = new Set<DataKind>()
    dataRegions.forEach((regions) =>
      regions.forEach((region) => present.add(region.kind))
    )
    return DATA_KINDS.filter((kind) => present.has(kind))
  }, [dataRegions])

  const scopedHits = useMemo(() => {
    if (!dataResult) return null
    if (dataScope === "all") return dataResult.all

    const scoped = new Map<string, SectionHit[]>()
    dataResult.incoming.forEach((hits, id) => {
      const kept = hits.filter(
        (hit) => dataKind === "any" || hit.kind === dataKind
      )
      const sections = STEP_SECTIONS.flatMap((section): SectionHit[] => {
        const inSection = kept.filter((hit) => hit.section === section)
        if (inSection.length === 0) return []
        return [
          {
            section,
            count: inSection.reduce((sum, hit) => sum + hit.count, 0),
            regions: inSection.map((hit) => hit.path)
          }
        ]
      })
      if (sections.length > 0) scoped.set(id, sections)
    })
    return scoped
  }, [dataResult, dataScope, dataKind])

  const visibleSteps = useMemo(
    () =>
      scopedHits
        ? filteredSteps.filter((step) => scopedHits.has(String(step._id)))
        : filteredSteps,
    [filteredSteps, scopedHits]
  )

  const hitCounts = useMemo(() => {
    const kinds: Record<DataKind, number> = {
      guidance: 0,
      knowledge: 0,
      store: 0,
      marketplace: 0,
      action: 0,
      skill: 0
    }
    const counts = { all: 0, incoming: 0, kinds }
    if (!dataResult) return counts
    filteredSteps.forEach((step) => {
      const id = String(step._id)
      dataResult.all.get(id)?.forEach((hit) => {
        counts.all += Math.max(hit.count, 1)
      })
      dataResult.incoming.get(id)?.forEach((hit) => {
        const n = Math.max(hit.count, 1)
        counts.incoming += n
        kinds[hit.kind] += n
      })
    })
    return counts
  }, [dataResult, filteredSteps])

  const occurrences = useMemo(() => {
    if (!scopedHits) return []
    const list: HitOccurrence[] = []
    visibleSteps.forEach((step) => {
      const stepId = String(step._id)
      scopedHits.get(stepId)?.forEach(({ section, count }) => {
        if (count === 0) {
          list.push({ stepId, section, index: null })
          return
        }
        for (let index = 0; index < count; index += 1) {
          list.push({ stepId, section, index })
        }
      })
    })
    return list
  }, [scopedHits, visibleSteps])

  const activeIndex = Math.min(activeHit, occurrences.length - 1)
  const activeOccurrence = activeIndex >= 0 ? occurrences[activeIndex] : null

  const dataSearchSummary = useMemo(() => {
    if (!dataResult) return ""
    const { needle } = dataResult
    if (hitCounts.all === 0) {
      return `"${needle}" hiçbir adımın verisinde bulunamadı.`
    }
    if (occurrences.length > 0) {
      const stepCount = new Set(occurrences.map((o) => o.stepId)).size
      return `${occurrences.length} eşleşme · ${stepCount} adım`
    }
    if (dataScope === "incoming") {
      const where =
        dataKind === "any"
          ? "gelen veride"
          : `${DATA_KIND_LABELS[dataKind]} verisinde`
      return `"${needle}" ${where} yok — yalnızca AI tarafında geçiyor (prompt, query, model çıktısı): ${hitCounts.all} eşleşme.`
    }
    return `"${needle}" bu filtreyle görünen adımlarda yok.`
  }, [dataResult, dataScope, dataKind, hitCounts, occurrences])

  const activeLocation = useMemo(() => {
    if (!activeOccurrence) return ""
    const step = (steps ?? []).find(
      (s) => String(s._id) === activeOccurrence.stepId
    )
    if (!step) return ""
    return `#${step.stepList ?? "-"} ${getStepTitle(step)} › ${SECTION_LABELS[activeOccurrence.section]}`
  }, [activeOccurrence, steps])

  const failedSteps = useMemo(
    () =>
      (steps ?? []).filter(
        (step) => step.status === AiMessageStepStatusEnum.Failed
      ),
    [steps]
  )
  const failedCount = failedSteps.length

  useEffect(() => {
    searchTextCache.current = new Map()
  }, [steps])

  useEffect(() => {
    const needle = dataQuery.trim()
    if (!needle) {
      setDataNeedle("")
      return
    }
    const timer = window.setTimeout(
      () => setDataNeedle(needle),
      DATA_SEARCH_DEBOUNCE_MS
    )
    return () => window.clearTimeout(timer)
  }, [dataQuery])

  /**
   * Walks the steps in chunks: a run can carry megabytes of request/response JSON, and scanning it
   * in one pass freezes the panel before the spinner paints. Every section is counted whatever the
   * scope, so switching scope never rescans.
   */
  useEffect(() => {
    if (!dataNeedle) {
      setDataResult(null)
      setIsScanning(false)
      return
    }

    setIsScanning(true)

    let cancelled = false
    const list = steps ?? []
    const lowered = dataNeedle.toLocaleLowerCase("tr")
    const all = new Map<string, SectionHit[]>()
    const incoming = new Map<string, RegionHit[]>()
    let index = 0
    let timer: number | undefined

    const scan = () => {
      if (cancelled) return

      const end = Math.min(index + DATA_SEARCH_CHUNK, list.length)
      for (; index < end; index += 1) {
        const step = list[index]
        const id = String(step._id)
        const sections: SectionHit[] = []
        STEP_SECTIONS.forEach((section) => {
          const text = sectionSearchText(
            searchTextCache.current,
            id,
            step,
            section
          )
          if (!text.includes(lowered)) return
          sections.push({
            section,
            count: countRenderedMatches(
              parseJsonIfPossible(stepSectionPayload(step, section)),
              dataNeedle
            )
          })
        })
        if (sections.length > 0) all.set(id, sections)

        const regionHits = (dataRegions.get(id) ?? []).flatMap(
          ({ value, ...region }): RegionHit[] => {
            const text = cachedSearchText(
              searchTextCache.current,
              `${id}:${region.section}:${region.path}`,
              value
            )
            if (!text.includes(lowered)) return []
            return [
              { ...region, count: countRenderedMatches(value, dataNeedle) }
            ]
          }
        )
        if (regionHits.length > 0) incoming.set(id, regionHits)
      }

      if (index < list.length) {
        timer = window.setTimeout(scan, 0)
        return
      }

      setDataResult({ needle: dataNeedle, all, incoming })
      setIsScanning(false)
    }

    timer = window.setTimeout(scan, 0)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [dataNeedle, steps, dataRegions])

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    getTryRate().then(setTryRate)
  }, [])

  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set())
  const isParentExpanded = (id: string) => expandedParents.has(id)
  const setParentExpanded = (id: string, expanded: boolean) => {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      expanded ? next.add(id) : next.delete(id)
      return next
    })
  }

  const allExpanded =
    visibleSteps.length > 0 &&
    visibleSteps.every((step) => expandedParents.has(String(step._id)))

  const toggleAllSteps = () => {
    setExpandedParents(
      allExpanded
        ? new Set()
        : new Set(visibleSteps.map((step) => String(step._id)))
    )
  }

  const [expandChildrenFor, setExpandChildrenFor] = useState<Set<string>>(
    new Set()
  )
  const isChildrenForcedOpen = (id: string) => expandChildrenFor.has(id)
  const setChildrenForcedOpen = (id: string, forced: boolean) => {
    setExpandChildrenFor((prev) => {
      const next = new Set(prev)
      forced ? next.add(id) : next.delete(id)
      return next
    })
  }

  const safeNum = (v: unknown) => {
    const n = typeof v === "number" ? v : parseFloat(String(v ?? 0))
    return Number.isFinite(n) ? n : 0
  }

  const aiUsageSteps = useMemo(() => {
    return (steps ?? [])
      .map((s) => {
        if (s?.step === AiAskQuestionTypeEnum.GetPineconeData) return null

        const res = s?.response as AiResponse | null
        if (!res) return null

        let usage: AiUsage | null = null

        if (
          res.usage &&
          (res.usage.inputToken !== undefined ||
            res.usage.outputToken !== undefined)
        ) {
          usage = {
            inputToken: safeNum(res.usage.inputToken),
            cachedInputToken: safeNum(res.usage.cachedInputToken),
            outputToken: safeNum(res.usage.outputToken),
            amount: safeNum(res.usage.amount)
          }
        } else {
          // Runs predating response.usage, summed over the chunked sub-calls.
          usage = legacyMetadataEntries(res).reduce<AiUsage | null>(
            (total, entry) => {
              const meta = entry?.usage_metadata
              if (!meta) return total
              const acc = total ?? {
                inputToken: 0,
                cachedInputToken: 0,
                outputToken: 0,
                amount: 0
              }
              acc.inputToken += safeNum(meta.input_tokens)
              acc.cachedInputToken += safeNum(
                meta.input_token_details?.cache_read
              )
              acc.outputToken += safeNum(meta.output_tokens)
              acc.amount += safeNum(meta.amount ?? entry.amount)
              return acc
            },
            null
          )
        }

        if (!usage) return null

        return {
          id: String(s._id),
          title: `${s.stepList ?? "-"} · ${getStepTitle(s)}`,
          ...usage
        }
      })
      .filter((u): u is AiStepDetail => u !== null)
  }, [steps])

  const runTotals = useMemo(() => getRunTotals(message), [message])
  const runOpen = message?.status === AiMessageStatusEnum.Running
  const totalsNote = runOpen
    ? "Run sürüyor — toplamlar run kapanınca yazılır."
    : "Bu run, toplamlar qpien-ai'de yazılmaya başlamadan önce kaydedildi."
  const missingValue = runOpen ? "sürüyor…" : "—"

  const durationStepDetails = useMemo(() => {
    return (steps ?? [])
      .map((s) => ({
        id: String(s._id),
        title: `${s.stepList ?? "-"} · ${getStepTitle(s)}`,
        durationMs: s?.durationMs ?? 0,
        container: isContainerDurationStep(s?.step)
      }))
      .filter((d) => d.durationMs > 0)
  }, [steps])
  const measuredStepDetails = useMemo(
    () => durationStepDetails.filter((d) => !d.container),
    [durationStepDetails]
  )
  const containerStepDetails = useMemo(
    () => durationStepDetails.filter((d) => d.container),
    [durationStepDetails]
  )
  const turnTimings = useMemo(() => getTurnTimings(steps), [steps])
  const totalDurationMs = runTotals?.durationMs ?? 0
  const totalAmount = runTotals?.amount ?? 0
  const totalTokens = runTotals
    ? runTotals.inputToken + runTotals.outputToken
    : 0

  const fmtMoney = (n: number) => fmtUsd(n)
  const fmtTryLocal = (n: number): string => fmtTry(n, tryRate)
  const fmtPct = (part: number, total: number) =>
    total > 0 ? ` — ${((part / total) * 100).toFixed(1)}%` : ""

  const aiStepDetails = aiUsageSteps

  const TotalsMissing = (
    <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
      {totalsNote}
    </Typography>
  )

  const TokenTooltipTitle = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
        <b>Tokens</b>
      </Typography>
      {runTotals ? (
        <>
          <Typography variant="caption" sx={{ display: "block" }}>
            {translate("input")}: <b>{runTotals.inputToken}</b>
            {runTotals.cachedInputToken > 0 && (
              <span style={{ opacity: 0.7 }}>
                {" "}
                ({runTotals.cachedInputToken} cache)
              </span>
            )}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
            {translate("output")}: <b>{runTotals.outputToken}</b>
          </Typography>
        </>
      ) : (
        TotalsMissing
      )}
      {aiStepDetails?.length > 0 && (
        <>
          <Typography sx={{ mt: 0.5 }}>{translate("ai_steps")}</Typography>
          <Box
            sx={{
              mt: 0.5,
              maxHeight: 240,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {aiStepDetails?.map((d) => {
              const stepTokens = d.inputToken + d.outputToken
              const tokenLabel =
                d.outputToken === 0
                  ? `${d.inputToken}`
                  : `${d.inputToken} / ${d.outputToken}`
              return (
                <Typography key={d.id} variant="caption">
                  • {d.title}: {tokenLabel}
                  <span style={{ opacity: 0.7 }}>
                    {fmtPct(stepTokens, totalTokens)}
                  </span>
                </Typography>
              )
            })}
          </Box>
        </>
      )}
    </Box>
  )

  const CostTooltipTitle = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
        <b>{translate("cost")}</b> (USD{tryRate > 0 ? " / ₺" : ""})
      </Typography>
      {runTotals ? (
        <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
          {translate("total")}:{" "}
          <b>
            {fmtMoney(totalAmount)}
            {fmtTryLocal(totalAmount)}
          </b>
          <span style={{ opacity: 0.7 }}>
            {" "}
            — {runTotals.llmCalls} LLM çağrısı
          </span>
        </Typography>
      ) : (
        TotalsMissing
      )}
      {aiStepDetails.length > 0 && (
        <>
          <Typography sx={{ mt: 0.5 }}>{translate("ai_steps")}</Typography>
          <Box
            sx={{
              mt: 0.5,
              maxHeight: 240,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {aiStepDetails.map((d) => (
              <Typography key={d.id} variant="caption">
                • {d.title}: {fmtMoney(d.amount)}
                {fmtTryLocal(d.amount)}
                <span style={{ opacity: 0.7 }}>
                  {fmtPct(d.amount, totalAmount)}
                </span>
              </Typography>
            ))}
          </Box>
        </>
      )}
    </Box>
  )

  const DurationTooltipTitle = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
        <b>{translate("duration")}</b>
      </Typography>
      {runTotals?.durationMs !== null && runTotals?.durationMs !== undefined ? (
        <Typography variant="caption" sx={{ display: "block" }}>
          {translate("total")}: <b>{(totalDurationMs / 1000).toFixed(2)} s</b>
          <span style={{ opacity: 0.7 }}>
            {" "}
            — run&apos;ın başlangıcından kapanışına, qpien-ai&apos;nin ölçtüğü
          </span>
        </Typography>
      ) : (
        TotalsMissing
      )}
      {turnTimings && (
        <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
          {`hazırlık ${(turnTimings.prepareMs / 1000).toFixed(2)} s · ajan ${(turnTimings.agentMs / 1000).toFixed(2)} s · teslim ${(turnTimings.settleMs / 1000).toFixed(2)} s`}
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 0.5, opacity: 0.7 }}
      >
        adım süreleri iç içe geçebilir; toplamları run süresi değildir
      </Typography>
      {measuredStepDetails.length > 0 && (
        <>
          <Typography sx={{ mt: 0.5 }}>{translate("ai_steps")}</Typography>
          <Box
            sx={{
              mt: 0.5,
              maxHeight: 240,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {measuredStepDetails.map((d) => (
              <Typography key={d.id} variant="caption">
                • {d.title}: {(d.durationMs / 1000).toFixed(2)} s
                <span style={{ opacity: 0.7 }}>
                  {fmtPct(d.durationMs, totalDurationMs)}
                </span>
              </Typography>
            ))}
          </Box>
        </>
      )}
      {containerStepDetails.length > 0 && (
        <>
          <Typography sx={{ mt: 0.5 }}>Kapsayıcı adımlar</Typography>
          <Box
            sx={{
              mt: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            {containerStepDetails.map((d) => (
              <Typography key={d.id} variant="caption">
                • {d.title}: {(d.durationMs / 1000).toFixed(2)} s
                <span style={{ opacity: 0.7 }}> — alt adımlarını kapsar</span>
              </Typography>
            ))}
          </Box>
        </>
      )}
    </Box>
  )

  const [expandedChildren, setExpandedChildren] = useState<
    Map<string, boolean>
  >(new Map())

  const childKey = (stepId: string, section: "req" | "res" | "src") =>
    `${stepId}:${section}`
  const getChildExpanded = (stepId: string, section: "req" | "res" | "src") => {
    const key = childKey(stepId, section)
    return (
      expandedChildren.get(key) ??
      (isChildrenForcedOpen(stepId) ? true : undefined)
    )
  }

  const setChildExpanded = (
    stepId: string,
    section: "req" | "res" | "src",
    next: boolean
  ) => {
    setExpandedChildren((prev) => {
      const m = new Map(prev)
      m.set(childKey(stepId, section), next)
      return m
    })
  }

  const resetChildOverridesFor = (stepId: string) => {
    setExpandedChildren((prev) => {
      const m = new Map(prev)
      Array.from(m.keys()).forEach((k) => {
        if (k.startsWith(`${stepId}:`)) m.delete(k)
      })
      return m
    })
  }

  const [explainAnchor, setExplainAnchor] = useState<{
    stepId: string
    el: HTMLElement
  } | null>(null)
  const [analysisOpen, setAnalysisOpen] = useState(false)
  const explainCacheRef = useRef<Map<string, AiAuditReport>>(new Map())

  const handleExplainClick = (
    e: React.MouseEvent<HTMLElement>,
    step: AiMessageSteps
  ) => {
    e.stopPropagation()
    e.preventDefault()
    setExplainAnchor({ stepId: String(step._id), el: e.currentTarget })
  }

  /**
   * Production Mongo is not reachable from a developer machine, so this is how a prod run
   * gets investigated locally: the run document and its full step list, verbatim, in the
   * envelope the `ai-run-debug` skill reads.
   */
  const debugBundle = useMemo(() => {
    if (!runId) return null
    return buildDebugBundle({
      kind: "ai-run",
      id: runId,
      data: {
        run: message ?? null,
        steps: steps ?? []
      }
    })
  }, [runId, message, steps])

  const listRef = useRef<HTMLDivElement | null>(null)
  const restoredForRef = useRef<string | null>(null)
  const stepNodes = useRef<Map<string, HTMLElement>>(new Map())

  const scrollKey = runId ? `${runId}:${activeView}` : null

  // Restore once the steps have painted, otherwise the list is still too short to scroll.
  useEffect(() => {
    const el = listRef.current
    if (!el || !scrollKey || isLoading) return
    if (restoredForRef.current === scrollKey) return
    restoredForRef.current = scrollKey

    el.scrollTop = stepsScrollMemory.get(scrollKey) ?? 0
  }, [scrollKey, isLoading, steps?.length])

  const [pendingJump, setPendingJump] = useState<string | null>(null)

  const jumpToStep = (stepId: string) => {
    restoredForRef.current = runId ? `${runId}:raw` : null
    setView("raw")
    if (!visibleSteps.some((step) => String(step._id) === stepId)) {
      setQuery("")
      setOnlyFailed(false)
      setDataQuery("")
    }
    setParentExpanded(stepId, true)
    setPendingJump(stepId)
  }

  // Waits a frame so a step that was filtered out or collapsed has painted before measuring it.
  useEffect(() => {
    if (!pendingJump) return
    const frame = requestAnimationFrame(() => {
      const list = listRef.current
      const node = stepNodes.current.get(pendingJump)
      setPendingJump(null)
      if (!list || !node) return
      list.scrollTop +=
        node.getBoundingClientRect().top - list.getBoundingClientRect().top
    })
    return () => cancelAnimationFrame(frame)
  }, [pendingJump, visibleSteps, activeView])

  const handleListScroll = () => {
    const el = listRef.current
    if (!el || !scrollKey) return
    stepsScrollMemory.set(scrollKey, el.scrollTop)
  }

  const activeMark = useRef<Element | null>(null)
  const focusTimer = useRef<number | undefined>(undefined)
  const focusedSearch = useRef<string | null>(null)

  useEffect(() => () => window.clearTimeout(focusTimer.current), [])

  const focusOccurrence = useCallback(
    (occurrence: HitOccurrence) => {
      const { stepId, section, index } = occurrence
      const sectionKey = `${stepId}:${section}`
      const sectionOpen =
        expandedChildren.get(sectionKey) ?? expandChildrenFor.has(stepId)
      const needsOpen = !expandedParents.has(stepId) || !sectionOpen
      if (needsOpen) {
        setExpandedParents((prev) => new Set(prev).add(stepId))
        setExpandedChildren((prev) => new Map(prev).set(sectionKey, true))
      }

      window.clearTimeout(focusTimer.current)
      focusTimer.current = window.setTimeout(
        () => {
          const container = listRef.current?.querySelector(
            `[data-search-section="${sectionKey}"]`
          )
          const mark =
            index === null
              ? null
              : (container?.querySelectorAll("mark.j-hit")[index] ?? null)

          activeMark.current?.classList.remove("j-hit-active")
          activeMark.current = mark
          mark?.classList.add("j-hit-active")
          ;(mark ?? container ?? stepNodes.current.get(stepId))?.scrollIntoView(
            { block: "center" }
          )
        },
        needsOpen ? EXPAND_SETTLE_MS : 0
      )
    },
    [expandedParents, expandedChildren, expandChildrenFor]
  )

  const hitGroups = useMemo(() => {
    const steps: number[] = []
    const sections: number[] = []
    occurrences.forEach((occurrence, index) => {
      const prev = occurrences[index - 1]
      if (!prev || prev.stepId !== occurrence.stepId) steps.push(index)
      if (
        !prev ||
        prev.stepId !== occurrence.stepId ||
        prev.section !== occurrence.section
      ) {
        sections.push(index)
      }
    })
    return { steps, sections }
  }, [occurrences])

  const groupPosition = (starts: number[]) => {
    let position = 0
    starts.forEach((start, index) => {
      if (start <= activeIndex) position = index
    })
    return position
  }

  const goToGroup = (starts: number[], delta: number) => {
    if (starts.length === 0) return
    const position = groupPosition(starts)
    const target = starts[(position + delta + starts.length) % starts.length]
    setActiveHit(target)
    focusOccurrence(occurrences[target])
  }

  const goToHit = (delta: number) => {
    if (occurrences.length === 0) return
    const next =
      (Math.max(activeIndex, 0) + delta + occurrences.length) %
      occurrences.length
    setActiveHit(next)
    focusOccurrence(occurrences[next])
  }

  // A live run keeps refetching its steps and re-running the search; unfolding the hits and landing
  // on the first one happens once per needle and scope, so the reader is not dragged back up.
  useEffect(() => {
    if (!dataResult || !scopedHits) {
      focusedSearch.current = null
      return
    }
    const searchKey = `${dataResult.needle}:${dataScope}:${dataKind}`
    if (focusedSearch.current === searchKey) return
    focusedSearch.current = searchKey

    setExpandedParents((prev) => {
      const next = new Set(prev)
      scopedHits.forEach((_, id) => next.add(id))
      return next
    })
    setExpandedChildren((prev) => {
      const next = new Map(prev)
      scopedHits.forEach((hits, id) => {
        hits.forEach(({ section }) => next.set(`${id}:${section}`, true))
      })
      return next
    })

    setActiveHit(0)
    if (occurrences.length > 0) focusOccurrence(occurrences[0])
  }, [
    dataResult,
    dataScope,
    dataKind,
    scopedHits,
    occurrences,
    focusOccurrence
  ])

  const changeDataScope = (next: DataSearchScope | null) => {
    if (!next) return
    setDataScope(next)
    writeDataScope(next)
  }

  if (!open) return null

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        borderLeft: 1,
        borderColor: "divider",
        height: "100%",
        display: "flex",
        bgcolor: "background.paper"
      }}
    >
      <Box
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: 1,
            borderColor: "divider"
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
              {translate("steps")}
            </Typography>
            {runId && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ fontFamily: MONO }}
                >
                  {runId}
                </Typography>
                <Tooltip title="Run ID kopyala" arrow>
                  <IconButton
                    size="small"
                    aria-label="Run ID kopyala"
                    onClick={() => copyToClipboard(runId)}
                  >
                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          {debugBundle && (
            <>
              <Tooltip
                title="Debug bundle'ı JSON olarak kopyala (/ai-debug için)"
                arrow
              >
                <IconButton
                  size="small"
                  aria-label="Debug bundle'ı JSON olarak kopyala"
                  onClick={() => {
                    void copyDebugBundle(debugBundle)
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Debug bundle'ı .json dosyası olarak indir" arrow>
                <IconButton
                  size="small"
                  aria-label="Debug bundle'ı JSON dosyası olarak indir"
                  onClick={() => downloadDebugBundle(debugBundle)}
                >
                  <DownloadIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {runId && (
            <Tooltip
              title="Tüm adımlar üzerinden karar denetimi — bu karar doğru mu, neden verildi?"
              arrow
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<TroubleshootIcon fontSize="small" />}
                onClick={() => setAnalysisOpen(true)}
              >
                Analiz
              </Button>
            </Tooltip>
          )}
          {navigation && <RunNavigator navigation={navigation} />}
          {backLabel ? (
            <Button
              size="small"
              variant="text"
              startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={onClose}
              sx={{ flexShrink: 0 }}
            >
              {backLabel}
            </Button>
          ) : (
            <IconButton size="small" aria-label="Kapat" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {message && <RunContextPanel message={message} role={roleResolution} />}

        {isLoading && (
          <Box sx={{ p: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!isLoading && (steps?.length ?? 0) > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: "divider"
              }}
            >
              <StatCell
                label={translate("duration")}
                value={
                  runTotals?.durationMs !== null &&
                  runTotals?.durationMs !== undefined
                    ? `${(totalDurationMs / 1000).toFixed(2)} s`
                    : missingValue
                }
                tooltip={DurationTooltipTitle}
              />
              <StatCell
                label="Token"
                value={
                  runTotals
                    ? `${runTotals.inputToken} / ${runTotals.outputToken}`
                    : missingValue
                }
                tooltip={TokenTooltipTitle}
              />
              <StatCell
                label={translate("cost")}
                value={
                  runTotals
                    ? `${fmtMoney(totalAmount)}${fmtTryLocal(totalAmount)}`
                    : missingValue
                }
                tooltip={CostTooltipTitle}
              />
              <StatCell
                label={translate("steps")}
                value={
                  runTotals
                    ? `${steps?.length ?? 0} (${runTotals.llmCalls} AI)`
                    : `${steps?.length ?? 0}`
                }
              />
            </Box>

            {(!!runId || hasSummary || hasSources) && (
              <Tabs
                value={activeView}
                onChange={(_, next: StepsView) => setView(next)}
                sx={{
                  px: 1,
                  minHeight: 36,
                  borderBottom: 1,
                  borderColor: "divider",
                  "& .MuiTab-root": {
                    minHeight: 36,
                    py: 0,
                    textTransform: "none",
                    fontSize: 13
                  }
                }}
              >
                {!!runId && <Tab value="why" label="Neden?" />}
                {hasSummary && <Tab value="summary" label="Özet" />}
                {hasSources && (
                  <Tab value="sources" label="Kullanılan kaynaklar" />
                )}
                <Tab
                  value="raw"
                  label={
                    <Box
                      component="span"
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      Ham adımlar · {steps?.length ?? 0}
                      {failedCount > 0 && (
                        <Box
                          component="span"
                          sx={{
                            px: 0.75,
                            borderRadius: 8,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "common.white",
                            bgcolor: "error.main"
                          }}
                        >
                          {failedCount}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </Tabs>
            )}

            {activeView === "raw" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderBottom: 1,
                  borderColor: "divider"
                }}
              >
                <TextField
                  size="small"
                  placeholder="Adım ara…"
                  inputProps={{ "aria-label": "Adım ara" }}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": { height: 32, fontSize: 13 }
                  }}
                />
                <Tooltip
                  title="Adım verilerinde ara — kapsamı ve eşleşmeler arası geçişi alttaki şeritten seç. Yazmayı bırakınca aranır."
                  arrow
                >
                  <TextField
                    size="small"
                    placeholder="Veri ara…"
                    inputProps={{ "aria-label": "Veri ara" }}
                    value={dataQuery}
                    onChange={(e) => setDataQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ fontSize: 16, color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: isSearchingData ? (
                        <InputAdornment position="end">
                          <CircularProgress size={13} thickness={5} />
                        </InputAdornment>
                      ) : undefined
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": { height: 32, fontSize: 13 }
                    }}
                  />
                </Tooltip>
                <Chip
                  size="small"
                  label={`Hata${failedCount ? ` · ${failedCount}` : ""}`}
                  color={onlyFailed ? "error" : "default"}
                  variant={onlyFailed ? "filled" : "outlined"}
                  onClick={() => setOnlyFailed(!onlyFailed)}
                />
                <Tooltip
                  title={allExpanded ? "Tüm adımları kapat" : "Tüm adımları aç"}
                  arrow
                >
                  <IconButton
                    size="small"
                    aria-label={
                      allExpanded ? "Tüm adımları kapat" : "Tüm adımları aç"
                    }
                    onClick={toggleAllSteps}
                  >
                    {allExpanded ? (
                      <UnfoldLessIcon fontSize="small" />
                    ) : (
                      <UnfoldMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {activeView === "raw" && isSearchingData && (
              <LinearProgress sx={{ height: 2 }} />
            )}

            {activeView === "raw" && !!dataQuery.trim() && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "background.default"
                }}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={dataScope}
                  onChange={(_, next: DataSearchScope | null) =>
                    changeDataScope(next)
                  }
                  aria-label="Veri arama kapsamı"
                  sx={{
                    flexShrink: 0,
                    "& .MuiToggleButton-root": {
                      py: 0.25,
                      px: 1,
                      fontSize: 11.5,
                      lineHeight: 1.5,
                      textTransform: "none"
                    }
                  }}
                >
                  {DATA_SCOPE_OPTIONS.map((option) => (
                    <Tooltip key={option.value} title={option.hint} arrow>
                      <ToggleButton value={option.value}>
                        {option.label}
                        {dataResult && !isSearchingData && (
                          <Box
                            component="span"
                            sx={{ ml: 0.5, fontFamily: MONO, opacity: 0.7 }}
                          >
                            {hitCounts[option.value]}
                          </Box>
                        )}
                      </ToggleButton>
                    </Tooltip>
                  ))}
                </ToggleButtonGroup>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {isSearchingData
                    ? "Veri aranıyor… adım verileri büyük olabilir, bu biraz sürebilir."
                    : dataSearchSummary}
                </Typography>
              </Box>
            )}

            {activeView === "raw" &&
              !!dataQuery.trim() &&
              !isSearchingData &&
              occurrences.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    columnGap: 1.5,
                    rowGap: 0.25,
                    px: 2,
                    py: 0.25,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "background.default"
                  }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 120,
                      fontFamily: MONO,
                      fontSize: 10.5,
                      color: "text.primary"
                    }}
                  >
                    {activeLocation}
                  </Typography>
                  <HitNavigator
                    label="Adım"
                    position={groupPosition(hitGroups.steps)}
                    total={hitGroups.steps.length}
                    onMove={(delta) => goToGroup(hitGroups.steps, delta)}
                  />
                  {hitGroups.sections.length > hitGroups.steps.length && (
                    <HitNavigator
                      label="Bölüm"
                      position={groupPosition(hitGroups.sections)}
                      total={hitGroups.sections.length}
                      onMove={(delta) => goToGroup(hitGroups.sections, delta)}
                    />
                  )}
                  <HitNavigator
                    label="Eşleşme"
                    position={activeIndex}
                    total={occurrences.length}
                    onMove={goToHit}
                  />
                </Box>
              )}

            {activeView === "raw" &&
              !!dataQuery.trim() &&
              dataScope === "incoming" &&
              availableKinds.length > 1 && (
                <Box
                  role="group"
                  aria-label="Gelen veri türü"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                    px: 2,
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "background.default"
                  }}
                >
                  {(["any", ...availableKinds] as DataKindFilter[]).map(
                    (kind) => {
                      const selected = dataKind === kind
                      const count =
                        kind === "any"
                          ? hitCounts.incoming
                          : hitCounts.kinds[kind]
                      return (
                        <Chip
                          key={kind}
                          size="small"
                          clickable
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          onClick={() => setDataKind(kind)}
                          label={`${kind === "any" ? "Hepsi" : DATA_KIND_LABELS[kind]}${
                            dataResult && !isSearchingData ? ` · ${count}` : ""
                          }`}
                          sx={{
                            height: 22,
                            fontSize: 11.5,
                            opacity:
                              dataResult && !isSearchingData && count === 0
                                ? 0.55
                                : 1
                          }}
                        />
                      )
                    }
                  )}
                </Box>
              )}
          </>
        )}

        <Box
          ref={listRef}
          onScroll={handleListScroll}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
        >
          {!isLoading && activeView === "why" && runId && (
            <AnswerExplanationView
              runId={runId}
              steps={steps}
              onJumpToStep={jumpToStep}
              onShowSources={hasSources ? () => setView("sources") : undefined}
            />
          )}

          {!isLoading && activeView === "summary" && (
            <>
              <FailureStrip steps={failedSteps} onJumpToStep={jumpToStep} />
              <V2RunOverview steps={steps} onJumpToStep={jumpToStep} />
              {hasSkillRun && <SkillRunTimeline steps={steps} />}
              {hasCommerceRun && !v2Run && (
                <CommerceRunTimeline steps={steps} />
              )}
            </>
          )}

          {!isLoading && activeView === "sources" && (
            <UsedSourcesView steps={steps} onJumpToStep={jumpToStep} />
          )}

          {activeView === "raw" && <StepLegend steps={steps} />}

          {activeView === "raw" &&
            isSearchingData &&
            visibleSteps.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  p: 3
                }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Veri aranıyor…
                </Typography>
              </Box>
            )}

          {activeView === "raw" &&
            visibleSteps.length === 0 &&
            !isLoading &&
            !isSearchingData && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 2, textAlign: "center" }}
              >
                Bu filtreye uyan adım yok.
              </Typography>
            )}

          {activeView === "raw" &&
            visibleSteps.map((s) => {
              const id = String(s._id)
              const stepCategory = getStepCategory(s)
              const stepTone = STEP_CATEGORY_TONES[stepCategory]
              const stepRole =
                String(s.step) === "AI_ROLE_RESOLUTION"
                  ? getRoleResolution([s])
                  : null
              const skillAudit = getSkillAuditPayload(s.response)
              const isSkillAudit = isSkillAuditStep(s)
              const childrenOpen = isChildrenForcedOpen(id)
              const failed = s.status === AiMessageStepStatusEnum.Failed
              const readable = hasReadableView(s)
              const explainedStep = s as AiMessageSteps & {
                summary?: string | null
                reason?: string | null
              }
              const stepHits = scopedHits?.get(id) ?? []
              const sectionHit = (section: StepSection) =>
                stepHits.find((hit) => hit.section === section)
              const highlightIn = (section: StepSection) =>
                sectionHit(section) ? dataResult?.needle : undefined
              const stepHitCount = stepHits.reduce(
                (sum, hit) => sum + Math.max(hit.count, 1),
                0
              )
              return (
                <Accordion
                  key={id}
                  ref={(node: HTMLElement | null) => {
                    if (node) stepNodes.current.set(id, node)
                    else stepNodes.current.delete(id)
                  }}
                  disableGutters
                  square
                  elevation={0}
                  slotProps={{
                    transition: { timeout: 120, unmountOnExit: true }
                  }}
                  expanded={isParentExpanded(id)}
                  onChange={(_, isExp) => setParentExpanded(id, isExp)}
                  sx={{
                    bgcolor: failed ? FAILED_TINT : stepTone.bg,
                    borderLeft: `3px solid ${stepTone.border}`,
                    borderBottom: 1,
                    borderColor: "divider",
                    borderLeftColor: stepTone.border,
                    "&::before": { display: "none" },
                    "&:hover": {
                      bgcolor: failed ? FAILED_TINT : stepTone.hoverBg
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      px: 2,
                      minHeight: 0,
                      "& .MuiAccordionSummary-content": {
                        my: 1,
                        alignItems: "center",
                        minWidth: 0
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        flexShrink: 0,
                        mr: 1.25,
                        bgcolor: failed ? "error.main" : "success.main"
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: MONO,
                        color: "text.disabled",
                        width: 22,
                        flexShrink: 0
                      }}
                    >
                      {s.stepList}
                    </Typography>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {getStepTitle(s)}
                      </Typography>
                      {explainedStep.summary && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block" }}
                        >
                          {readableStepText(
                            String(s.step),
                            explainedStep.summary
                          )}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        ml: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        flexShrink: 0
                      }}
                    >
                      {stepHitCount > 0 && (
                        <Tooltip title={`${stepHitCount} eşleşme`} arrow>
                          <Box
                            component="span"
                            sx={{
                              mr: 0.5,
                              px: 0.75,
                              borderRadius: 8,
                              fontSize: 10.5,
                              fontWeight: 700,
                              fontFamily: MONO,
                              bgcolor:
                                activeOccurrence?.stepId === id
                                  ? "#ff9800"
                                  : HIT_BADGE_BG
                            }}
                          >
                            {stepHitCount}
                          </Box>
                        </Tooltip>
                      )}
                      {!!s.durationMs && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: MONO, mr: 0.5 }}
                        >
                          {(s.durationMs / 1000).toFixed(2)}s
                        </Typography>
                      )}
                      {stepRole && (
                        <Box sx={{ mr: 0.25 }}>
                          <RoleChip role={stepRole.resolvedRole} />
                        </Box>
                      )}
                      {isSkillAudit && (
                        <Chip
                          label="Skill"
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10, mr: 0.25 }}
                        />
                      )}
                      {isLlmStep(s.response) && (
                        <Tooltip
                          title="Bu adımın kararı doğru mu? Neden böyle karar verildi?"
                          arrow
                        >
                          <IconButton
                            size="small"
                            aria-label="Adım kararını açıkla"
                            onClick={(e) => handleExplainClick(e, s)}
                          >
                            <PsychologyAltIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          childrenOpen
                            ? "İç bölümleri kapat"
                            : "İç bölümleri aç"
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          aria-label={
                            childrenOpen
                              ? "Adım iç bölümlerini kapat"
                              : "Adım iç bölümlerini aç"
                          }
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            if (childrenOpen && isParentExpanded(id)) {
                              setChildrenForcedOpen(id, false)
                              resetChildOverridesFor(id)
                            } else {
                              setParentExpanded(id, true)
                              setChildrenForcedOpen(id, true)
                              resetChildOverridesFor(id)
                            }
                          }}
                        >
                          {childrenOpen ? (
                            <UnfoldLessIcon sx={{ fontSize: 17 }} />
                          ) : (
                            <UnfoldMoreIcon sx={{ fontSize: 17 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ px: 2, pt: 0, overflowWrap: "anywhere" }}
                  >
                    {s.status && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {translate("status")}:{" "}
                        {s.status === AiMessageStepStatusEnum.Success ? (
                          <Typography
                            component="span"
                            sx={{ color: "success.main" }}
                          >
                            {translate(s.status)}
                          </Typography>
                        ) : (
                          <Typography
                            component="span"
                            sx={{ color: "error.main" }}
                          >
                            {translate(s.status)}
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {!!s.durationMs && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {`${translate("duration")}: ${(s.durationMs / 1000).toFixed(2)} s`}
                      </Typography>
                    )}
                    {explainedStep.reason && (
                      <Box
                        sx={{
                          mb: 1.5,
                          p: 1.25,
                          borderRadius: 1,
                          bgcolor: "action.hover"
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          Neden
                        </Typography>
                        <Typography variant="body2">
                          {readableStepText(
                            String(s.step),
                            explainedStep.reason
                          )}
                        </Typography>
                      </Box>
                    )}
                    {stepRole && (
                      <Box sx={{ mb: 1.5 }}>
                        <RoleStepDetail role={stepRole} />
                      </Box>
                    )}
                    {isSkillAudit && skillAudit && (
                      <Box
                        sx={{
                          mb: 1.5,
                          p: 1.25,
                          borderRadius: 1,
                          bgcolor: "rgba(156, 39, 176, 0.04)",
                          border: 1,
                          borderColor: "rgba(156, 39, 176, 0.2)"
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          Skill audit
                        </Typography>
                        <Typography variant="body2">
                          Run: {skillAudit.skillRunId}
                        </Typography>
                        {skillAudit.command && (
                          <Typography variant="body2">
                            Komut: {skillAudit.command}
                          </Typography>
                        )}
                        {skillAudit.route && (
                          <Typography variant="body2">
                            Rota: {skillAudit.route}
                          </Typography>
                        )}
                        {!!skillAudit.instructionAnchorIds?.length && (
                          <Box
                            sx={{
                              mt: 0.75,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5
                            }}
                          >
                            {skillAudit.instructionAnchorIds.map((anchorId) => (
                              <Chip
                                key={anchorId}
                                label={anchorId}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                    {readable && isParentExpanded(id) && (
                      <StepReadableView step={s} context={readableContext} />
                    )}
                    {readable && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 0.5,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.6,
                          textTransform: "uppercase",
                          color: "text.secondary"
                        }}
                      >
                        Ham veri
                      </Typography>
                    )}
                    <JsonAccordion
                      title={
                        String(s.step) === "COMMERCE_MCP_TOOL_CALL"
                          ? "Tool'a Giden Request"
                          : String(s.step) === "COMMERCE_AGENT_ROUND"
                            ? "Agent'a Giden Request"
                            : "Request"
                      }
                      data={s.request}
                      highlight={highlightIn("req")}
                      highlightRegions={sectionHit("req")?.regions}
                      hitCount={sectionHit("req")?.count}
                      searchKey={`${id}:req`}
                      expanded={getChildExpanded(id, "req")}
                      onChange={(next) => setChildExpanded(id, "req", next)}
                    />

                    <JsonAccordion
                      title={
                        String(s.step) === "COMMERCE_MCP_TOOL_CALL"
                          ? "Tool'dan Gelen Response (Raw, Hassas Alanlar Gizli)"
                          : String(s.step) === "COMMERCE_AGENT_ROUND"
                            ? "Agent'tan Gelen Response"
                            : "Response"
                      }
                      data={s.response}
                      highlight={highlightIn("res")}
                      highlightRegions={sectionHit("res")?.regions}
                      hitCount={sectionHit("res")?.count}
                      searchKey={`${id}:res`}
                      expanded={getChildExpanded(id, "res")}
                      onChange={(next) => setChildExpanded(id, "res", next)}
                    />

                    {s.source && (
                      <JsonAccordion
                        title="Source"
                        data={s.source}
                        highlight={highlightIn("src")}
                        highlightRegions={sectionHit("src")?.regions}
                        hitCount={sectionHit("src")?.count}
                        searchKey={`${id}:src`}
                        expanded={getChildExpanded(id, "src")}
                        onChange={(next) => setChildExpanded(id, "src", next)}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              )
            })}
        </Box>
      </Box>

      <Popover
        open={!!explainAnchor}
        anchorEl={explainAnchor?.el ?? null}
        onClose={() => setExplainAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {explainAnchor && (
          <StepExplainPanel
            key={explainAnchor.stepId}
            step={(steps ?? []).find(
              (s) => String(s._id) === explainAnchor.stepId
            )}
            cache={explainCacheRef.current}
          />
        )}
      </Popover>

      {runId && (
        <AiAnalysisDialog
          open={analysisOpen}
          onClose={() => setAnalysisOpen(false)}
          aiMessageId={runId}
        />
      )}
    </Box>
  )
}

/** One level of match navigation — the steps, the Request/Response/Source sections, or single matches. */
function HitNavigator({
  label,
  position,
  total,
  onMove
}: {
  label: string
  position: number
  total: number
  onMove: (delta: number) => void
}) {
  const lower = label.toLocaleLowerCase("tr")
  return (
    <Box
      role="group"
      aria-label={`${label} gezinme`}
      sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10.5, mr: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        aria-live="polite"
        sx={{ fontFamily: MONO, fontSize: 11 }}
      >
        {position + 1}/{total}
      </Typography>
      <Tooltip title={`Önceki ${lower}`} arrow>
        <span>
          <IconButton
            size="small"
            aria-label={`Önceki ${lower}`}
            disabled={total < 2}
            onClick={() => onMove(-1)}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`Sonraki ${lower}`} arrow>
        <span>
          <IconButton
            size="small"
            aria-label={`Sonraki ${lower}`}
            disabled={total < 2}
            onClick={() => onMove(1)}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

function ContextBubble({
  label,
  text,
  tone
}: {
  label: string
  text: string
  tone: "question" | "answer"
}) {
  const isQuestion = tone === "question"

  return (
    <Box
      sx={{
        mt: 0.75,
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        borderLeft: 3,
        borderColor: isQuestion ? "grey.400" : "primary.main",
        bgcolor: isQuestion ? "#ededed" : "#e5f0fd"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          sx={{ color: isQuestion ? "text.secondary" : "#0b3d70" }}
        >
          {label}
        </Typography>
        <Tooltip title="Metni kopyala" arrow>
          <IconButton
            size="small"
            onClick={() => copyToClipboard(text)}
            sx={{ p: 0.25 }}
          >
            <ContentCopyIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "#000",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "anywhere"
        }}
      >
        {text}
      </Typography>
    </Box>
  )
}

/**
 * The step log takes over the conversation pane, so it carries the turn it explains with it:
 * what came in, what went out, and whether the run failed.
 */
function RunContextPanel({
  message,
  role
}: {
  message: AiMessages
  role: RoleResolutionSummary | null
}) {
  const { translate } = useTranslate()
  const [expanded, setExpanded] = useState(readContextOpen)

  const toggle = () => {
    setExpanded((prev) => {
      writeContextOpen(!prev)
      return !prev
    })
  }

  const answers = getAnswers(message)
  const failed = message.status === AiMessageStatusEnum.Failed
  const running = message.status === AiMessageStatusEnum.Running
  const question = (message.question ?? "").trim()
  const mediaCount = message.questionMedia?.length ?? 0
  const preview =
    question || (mediaCount > 0 ? `${mediaCount} görsel` : "Soru metni yok")

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.default"
      }}
    >
      <Box
        onClick={toggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 0.75,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" }
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: failed
              ? "error.main"
              : running
                ? "warning.main"
                : "success.main"
          }}
        />
        <Typography
          variant="caption"
          sx={{
            flexShrink: 0,
            textTransform: "uppercase",
            fontSize: 10,
            letterSpacing: 0.6,
            color: "text.secondary"
          }}
        >
          Bu mesaj
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            visibility: expanded ? "hidden" : "visible"
          }}
        >
          {preview}
        </Typography>
        {role && <RoleChip role={role.resolvedRole} />}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ flexShrink: 0, fontFamily: MONO }}
        >
          {dayjs(message.createdAt as Date).format("DD MMM HH:mm")}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            flexShrink: 0,
            color: "text.secondary",
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform .15s"
          }}
        />
      </Box>

      {expanded && (
        <Box sx={{ px: 2, pb: 1.25, maxHeight: 300, overflowY: "auto" }}>
          {role && <RoleLine role={role} />}

          {(question || mediaCount > 0) && (
            <ContextBubble
              tone="question"
              label={
                mediaCount > 0
                  ? `Gelen soru · ${mediaCount} görsel`
                  : "Gelen soru"
              }
              text={question || "(yalnızca görsel)"}
            />
          )}

          {running && answers.length === 0 && (
            <Box sx={{ mt: 0.75 }}>
              <Chip
                size="small"
                icon={<CircularProgress size={12} thickness={6} />}
                label={translate("ai_logs_generating_answer")}
              />
            </Box>
          )}

          {answers.map((answer, idx) => (
            <ContextBubble
              key={`${answer.messageType ?? "answer"}-${idx}`}
              tone="answer"
              label={
                answer.messageType && ANSWER_LABEL_KEYS[answer.messageType]
                  ? `Üretilen cevap · ${translate(
                      ANSWER_LABEL_KEYS[answer.messageType] as string
                    )}`
                  : "Üretilen cevap"
              }
              text={answer.content ?? ""}
            />
          ))}

          {!running && answers.length === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75 }}
            >
              Bu mesaj için cevap üretilmedi.
            </Typography>
          )}

          {message.error && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.75, color: "error.main" }}
            >
              {message.error}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              mt: 0.75
            }}
          >
            <Typography
              variant="caption"
              color="text.disabled"
              noWrap
              sx={{ fontFamily: MONO, fontSize: 10 }}
            >
              {translate("ai_logs_message_id")}: {message.questionId}
            </Typography>
            <Tooltip title="Mesaj ID kopyala" arrow>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(String(message.questionId))}
              >
                <ContentCopyIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  )
}

function StepExplainPanel({
  step,
  cache
}: {
  step: AiMessageSteps | undefined
  cache: Map<string, AiAuditReport>
}) {
  const stepId = step ? String(step._id) : ""
  const [question, setQuestion] = useState("")
  const [askedQuestion, setAskedQuestion] = useState<string | null>(null)
  const [report, setReport] = useState<AiAuditReport | null>(() =>
    stepId ? (cache.get(stepId) ?? null) : null
  )
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const requestSeq = useRef(0)

  const runExplain = async (customQuestion?: string) => {
    if (!step) return

    const trimmed = customQuestion?.trim()
    if (!trimmed && cache.has(stepId)) {
      setAskedQuestion(null)
      setReport(cache.get(stepId)!)
      setError(null)
      return
    }

    const seq = ++requestSeq.current
    setIsLoading(true)
    setError(null)
    setReport(null)
    setAskedQuestion(trimmed ?? null)

    const response = await explainAiMessageStepQuery({
      request: step.request,
      response: step.response,
      ...(trimmed ? { question: trimmed } : {})
    })

    if (seq !== requestSeq.current) return
    setIsLoading(false)

    if (response.isSuccess && response.data) {
      setReport(response.data)
      if (!trimmed) {
        cache.set(stepId, response.data)
      }
    } else {
      setError(response.errorMessage ?? "Açıklama alınamadı.")
    }
  }

  // Clicking the icon is the request — the verdict loads without a second click.
  useEffect(() => {
    if (!stepId || cache.has(stepId)) return
    void runExplain()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId])

  const handleAsk = () => {
    if (!question.trim() || isLoading) return
    void runExplain(question)
  }

  return (
    <Box
      sx={{
        p: 2,
        width: 560,
        maxWidth: "90vw",
        maxHeight: 560,
        overflowY: "auto"
      }}
    >
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 3
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="caption" color="text.secondary">
            Adım denetleniyor…
          </Typography>
        </Box>
      )}

      {error && !isLoading && (
        <Typography variant="body2" sx={{ color: "error.main" }}>
          {error}
        </Typography>
      )}

      {report && !isLoading && (
        <AiVerdictReport report={report} question={askedQuestion} />
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 2,
          pt: 1.5,
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Bu adım hakkında soru sor…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              handleAsk()
            }
          }}
        />
        <Tooltip title="Soruyu sor" arrow>
          <span>
            <IconButton
              color="primary"
              size="small"
              onClick={handleAsk}
              disabled={isLoading || !question.trim()}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {askedQuestion && (
          <Tooltip title="Genel karar denetimine dön" arrow>
            <span>
              <IconButton
                size="small"
                disabled={isLoading}
                onClick={() => {
                  setQuestion("")
                  void runExplain()
                }}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    console.error("Failed to copy:", err)
    return false
  }
}

function Highlighted({ text, needle }: { text: string; needle?: string }) {
  const target = needle?.trim() ?? ""
  const offsets = target ? findMatchOffsets(text, target) : []
  if (offsets.length === 0) return <>{text}</>

  const parts: React.ReactNode[] = []
  let cursor = 0
  offsets.forEach((at) => {
    if (at > cursor) parts.push(text.slice(cursor, at))
    parts.push(
      <mark key={at} className="j-hit">
        {text.slice(at, at + target.length)}
      </mark>
    )
    cursor = at + target.length
  })
  parts.push(text.slice(cursor))

  return <>{parts}</>
}

const COPY_HINT = "Değeri kopyalamak için tıkla"

/**
 * Plain elements, not MUI ones: a step payload runs to thousands of nodes, and a Tooltip, ripple
 * button or `sx` class per key is what made the panel lag on every keystroke.
 */
const JsonRenderer = memo(function JsonRenderer({
  data,
  onCopyValue,
  highlight,
  highlightRegions
}: {
  data: unknown
  onCopyValue?: (value: unknown) => void
  highlight?: string
  highlightRegions?: ReadonlyArray<string>
}) {
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(
    () => new Set()
  )

  const toggleKey = (key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleKeyClick = async (value: unknown) => {
    const success = await copyToClipboard(JSON.stringify(value, null, 2))
    if (success && onCopyValue) {
      onCopyValue(value)
    }
  }

  const needleAt = (path: string, isKey: boolean) =>
    !highlightRegions ||
    highlightRegions.some((region) => isInsideRegion(path, region, isKey))
      ? highlight
      : undefined

  const renderKey = (name: string, value: unknown, path: string) => (
    <span
      className="j-key j-copy"
      title={COPY_HINT}
      onClick={() => void handleKeyClick(value)}
    >
      &quot;
      <Highlighted text={name} needle={needleAt(path, true)} />
      &quot;
    </span>
  )

  const renderToggle = (key: string, open: boolean) => (
    <button type="button" className="j-toggle" onClick={() => toggleKey(key)}>
      {open ? (
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      ) : (
        <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
      )}
    </button>
  )

  const renderValue = (value: unknown, key: string): JSX.Element => {
    if (value === null) {
      return <span className="j-null">null</span>
    }

    if (typeof value === "boolean") {
      return <span className="j-bool">{value.toString()}</span>
    }

    if (typeof value === "number") {
      return (
        <span className="j-number">
          <Highlighted text={String(value)} needle={needleAt(key, false)} />
        </span>
      )
    }

    if (typeof value === "string") {
      return (
        <span className="j-string">
          &quot;
          <Highlighted text={value} needle={needleAt(key, false)} />
          &quot;
        </span>
      )
    }

    if (Array.isArray(value)) {
      const isExpanded = !collapsedKeys.has(key)
      return (
        <span>
          {renderToggle(key, isExpanded)}
          <span>[</span>
          {isExpanded && (
            <div className="j-indent">
              {value.map((item, idx) => (
                <div key={idx}>
                  {renderValue(item, `${key}[${idx}]`)}
                  {idx < value.length - 1 && ","}
                </div>
              ))}
            </div>
          )}
          {!isExpanded && value.length > 0 && (
            <span className="j-null"> {value.length} items </span>
          )}
          <span>]</span>
        </span>
      )
    }

    if (typeof value === "object") {
      const isExpanded = !collapsedKeys.has(key)
      const entries = Object.entries(value as Record<string, unknown>)
      return (
        <span>
          {renderToggle(key, isExpanded)}
          <span>{"{"}</span>
          {isExpanded && (
            <div className="j-indent">
              {entries.map(([k, v], idx) => (
                <div key={k}>
                  {renderKey(k, v, `${key}.${k}`)}
                  <span>: </span>
                  {renderValue(v, `${key}.${k}`)}
                  {idx < entries.length - 1 && ","}
                </div>
              ))}
            </div>
          )}
          {!isExpanded && entries.length > 0 && (
            <span className="j-null"> {entries.length} keys </span>
          )}
          <span>{"}"}</span>
        </span>
      )
    }

    return <span>{String(value)}</span>
  }

  if (typeof data === "object" && data !== null && !Array.isArray(data)) {
    const entries = Object.entries(data as Record<string, unknown>)
    return (
      <div>
        <span>{"{"}</span>
        <div className="j-indent">
          {entries.map(([key, value], idx) => (
            <div key={key}>
              {renderKey(key, value, key)}
              <span>: </span>
              {renderValue(value, key)}
              {idx < entries.length - 1 && ","}
            </div>
          ))}
        </div>
        <span>{"}"}</span>
      </div>
    )
  }

  return <div>{renderValue(data, "root")}</div>
})

export function JsonAccordion({
  title,
  data,
  defaultExpanded = false,
  expanded,
  onChange,
  highlight,
  highlightRegions,
  hitCount,
  searchKey
}: JsonAccordionProps) {
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const parsed = useMemo(() => parseJsonIfPossible(data), [data])

  const handleCopyAll = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const jsonString = JSON.stringify(parsed, null, 2)
    const success = await copyToClipboard(jsonString)
    if (success) {
      setSnackbarMessage("Tüm veri kopyalandı!")
      setSnackbarOpen(true)
    }
  }

  const handleCopyValue = useCallback(() => {
    setSnackbarMessage("Değer kopyalandı!")
    setSnackbarOpen(true)
  }, [])

  return (
    <>
      <Accordion
        key={expanded ? "expanded" : "collapsed"}
        disableGutters
        elevation={0}
        slotProps={{ transition: { timeout: 120, unmountOnExit: true } }}
        defaultExpanded={defaultExpanded}
        expanded={expanded ?? undefined}
        onChange={(_, isExp) => onChange?.(isExp)}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1.5,
          overflow: "hidden",
          mb: 1,
          "&::before": { display: "none" },
          "&.Mui-expanded": { mb: 1 }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            px: 1.25,
            minHeight: 0,
            bgcolor: "grey.50",
            borderBottom: 1,
            borderColor: "transparent",
            "&.Mui-expanded": { borderColor: "divider" },
            "&:hover": { bgcolor: "grey.100" },
            "& .MuiAccordionSummary-content": { my: 1, minWidth: 0 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              minWidth: 0,
              pr: 1
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0
              }}
            >
              <Typography variant="body2" fontWeight={600} noWrap>
                {title}
              </Typography>
              {!!hitCount && (
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    borderRadius: 8,
                    fontSize: 10.5,
                    fontWeight: 700,
                    fontFamily: MONO,
                    flexShrink: 0,
                    bgcolor: HIT_BADGE_BG
                  }}
                >
                  {hitCount} eşleşme
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="Tüm veriyi kopyala" arrow>
                <IconButton
                  size="small"
                  onClick={handleCopyAll}
                  component="span"
                >
                  <ContentCopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1 }}>
          <Box
            component="pre"
            data-search-section={searchKey}
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 12,
              p: 1.5,
              bgcolor: "grey.100",
              borderRadius: 1.5,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              overflowX: "auto",
              maxHeight: 420,
              "& .j-key": { color: "rgb(136, 19, 145)" },
              "& .j-string": { color: "rgb(34, 134, 58)" },
              "& .j-number": { color: "rgb(28, 98, 164)" },
              "& .j-bool": { color: "rgb(181, 80, 21)" },
              "& .j-null": { color: "rgb(128, 128, 128)" },
              "& .j-indent": { pl: 2 },
              "& mark.j-hit": {
                bgcolor: HIT_BADGE_BG,
                color: "inherit",
                borderRadius: "2px",
                px: "1px"
              },
              "& mark.j-hit-active": {
                bgcolor: "#ff9800",
                color: "#000",
                outline: "2px solid #e65100"
              },
              "& .j-copy": { cursor: "pointer", borderRadius: "3px" },
              "& .j-copy:hover": { backgroundColor: "rgba(136, 19, 145, 0.1)" },
              "& .j-toggle": {
                p: 0,
                mr: 0.5,
                border: 0,
                bgcolor: "transparent",
                color: "inherit",
                cursor: "pointer",
                verticalAlign: "middle",
                lineHeight: 0
              }
            }}
          >
            <JsonRenderer
              data={parsed}
              onCopyValue={handleCopyValue}
              highlight={highlight}
              highlightRegions={highlightRegions}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  )
}
